import * as React from 'react';
import { t } from '@lingui/core/macro';
import Button from 'components/Button';
import { tenantConfig } from 'utils/tenant';
import { myCameraStream } from 'state/video';
import { needsVoipMediaRecovery, retryVoipMedia, voipRtcConnection } from 'utils/ui/rtc';
import { classNames } from 'utils/ui/css';
import { isVoipEnabled, userSession } from 'state/session';

type TSessionControls = {
  voipActive?: boolean;
  children?: React.ReactNode;
};

const SessionControls: React.FC<TSessionControls> = ({ voipActive = false, children }) => {
  /** This component can be rendered as a part of the VOIP WC and as a child of it,
   * it would have these hooks invoked at the parent level already */
  if (!voipActive) {
    tenantConfig().useState();
    myCameraStream().useState();
    userSession().useState();
  }

  // AVS-4302: a receive-only standby (another tab of THIS dealer owns the VOIP master slot) has
  // no local camera/mic — its face is the master's loopback and its only affordance is "Take over".
  // So the camera/mic toggles are meaningless on a standby (and were merely disabled, which still
  // reads as actionable). Hide them entirely. A master / customer / flag-off tab is not a standby,
  // so it keeps its toggles exactly as before.
  const isStandby = userSession().isVoipStandby;

  const hasLocalStream = myCameraStream().stream instanceof MediaStream;

  const canRenderVoipButtons =
    !isStandby && (voipActive || (tenantConfig().isAllowed('voip') && hasLocalStream && isVoipEnabled()));

  // `needsVoipMediaRecovery()` owns the recovery rule (business logic stays out of the
  // component); an audio-only stream with a visible camera toggle deliberately gets no retry
  // control — the repaired toggle is already the recovery path.
  const canRenderMediaRetry = canRenderVoipButtons && voipActive && needsVoipMediaRecovery();

  return (
    <>
      {/* Without a local stream the toggles are dead (`toggleMedia` early-returns) — drop them
          and offer the recovery control below instead. */}
      {canRenderVoipButtons && hasLocalStream && (
        <>
          <Button
            icon="ex3d-icon--camera"
            part="voip-button--camera"
            data-testid="voip-button-camera"
            svgClassName="bg-black"
            ariaLabel={myCameraStream().get('isVideoEnabled') ? t`Turn camera off` : t`Turn camera on`}
            className={classNames(
              'stream-header-icon-camera',
              myCameraStream().get('isVideoEnabled')
                ? 'ex3d-enabled ex3d-icon--state__on'
                : 'ex3d-disabled stream-header-icon-camera-disabled ex3d-icon--state__off',
              myCameraStream().get('isVideoAllowed') ? '' : 'hidden'
            )}
            onClick={() => {
              voipRtcConnection.toggleMedia('video');
            }}
          />
          <Button
            icon="ex3d-icon--mic"
            part="voip-button--mic"
            data-testid="voip-button-mic"
            svgClassName="bg-black"
            ariaLabel={myCameraStream().get('isAudioEnabled') ? t`Mute microphone` : t`Unmute microphone`}
            className={classNames(
              'stream-header-icon-mic',
              myCameraStream().get('isAudioEnabled')
                ? 'ex3d-enabled ex3d-icon--state__on'
                : 'ex3d-disabled stream-header-icon-mic-disabled ex3d-icon--state__off',
              myCameraStream().get('isAudioAllowed') ? '' : 'hidden'
            )}
            onClick={() => {
              voipRtcConnection.toggleMedia('audio');
            }}
          />
        </>
      )}
      {canRenderMediaRetry && (
        <Button
          icon="ex3d-icon--update"
          part="voip-button--retry-media"
          data-testid="voip-button-retry-media"
          svgClassName="bg-black"
          ariaLabel={t`Retry camera and microphone`}
          // Existing design-system action-state classes — no new CSS, no invented colour.
          className="ex3d-enabled ex3d-icon--state__on"
          onClick={() => {
            void retryVoipMedia();
          }}
        />
      )}
      {children}
    </>
  );
};

export default SessionControls;
